/**
 * components/landing/SiteHeader.jsx — Floating glass navigation for the
 * Kalki Ai Cloud corporate site.
 *
 * Requirements (Phase 1 PRD + Final Production Readiness PRD):
 *   • Sticky (pinned to top)
 *   • Rounded glass container
 *   • Blur background
 *   • Transparent initially, solid glass while scrolling
 *   • Smooth animation on state change
 *   • Premium shadow when scrolled
 *   • Active navigation indicator
 *   • Mega-menu on Platform + Resources
 *   • Mobile full-screen menu
 *   • Login (existing tenant customers only — links into /login)
 *   • Schedule Demo
 *   • Launch Platform CTA
 *
 * Primary nav (per Final Production Readiness PRD §1):
 *   Platform · AI · Trading · White-Label · Pricing · Resources · Company · Contact
 *
 * All nav destinations are Kalki Ai Cloud-only routes (see /app/(brand)/…).
 * Tenant routes are NEVER surfaced from here.
 */

import { useEffect, useState } from 'react'
import Link from '@/shims/NextLink'
import { usePathname } from '@/shims/NextNavigation'
import clsx from 'clsx'
import {
  Menu, X, ChevronDown, Layers, Cpu, BarChart3, Building2,
  Sparkles, Shield, Zap, LineChart, Users, GraduationCap, FileText,
  BookOpen, Rocket, ShieldCheck, MessageSquare,
} from 'lucide-react'
import BrandLogo from './BrandLogo'
import { GradientButton, GhostButton } from './design-system'

// ─── Navigation registry ──────────────────────────────────────────
// Kept in this file (not imported) so the site header is a single
// self-contained component during Phase 1.  Move to /lib/nav.js in a
// later phase if reused across other layouts.
const PLATFORM_MENU = [
  { label: 'Platform Overview', href: '/platform',    icon: Layers,    desc: 'The full Kalki Ai Cloud SaaS stack' },
  { label: 'AI Trading Core',   href: '/ai',          icon: Cpu,       desc: 'Model orchestration + LLM copilots' },
  { label: 'Trading Engine',    href: '/trading',     icon: BarChart3, desc: 'Multi-exchange execution + risk' },
  { label: 'White-Label',       href: '/white-label', icon: Building2, desc: 'Launch your own branded platform' },
  { label: 'AI Mentor',         href: '/ai',                        icon: Sparkles,       desc: 'RAG assistant with citations' },
  { label: 'Risk & Compliance', href: '/trust',                     icon: Shield,         desc: 'Guardrails at every layer' },
  { label: 'Execution',         href: '/trading',                   icon: Zap,            desc: 'Sub-second order routing' },
  { label: 'Analytics',         href: '/platform#platform-components', icon: LineChart,   desc: 'Per-tenant + platform-wide' },
]
const RESOURCES_MENU = [
  { label: 'Blog & Insight',href: '/resources',   icon: FileText,     desc: 'Product updates + market notes' },
  { label: 'Get Started',   href: '/get-started', icon: Rocket,       desc: 'Launch your platform in weeks' },
  { label: 'Trust Center',  href: '/trust',       icon: ShieldCheck,  desc: 'Security, privacy, compliance' },
  { label: 'Academy',       href: '/platform',    icon: GraduationCap, desc: 'Built-in trader onboarding' },
  { label: 'Multi-tenant Admin', href: '/white-label', icon: Users,   desc: 'Isolated tenants with RBAC' },
]

const PRIMARY_NAV = [
  { label: 'Platform',   megaKey: 'platform' },
  { label: 'AI',         href: '/ai' },
  { label: 'Trading',    href: '/trading' },
  { label: 'White-Label',href: '/white-label' },
  { label: 'Pricing',    href: '/pricing' },
  // Resources used to open a hover mega-menu but the 12px gap
  // between the trigger button and the absolute-positioned panel
  // caused onMouseLeave on the header to fire before onMouseEnter
  // on the panel — production visitors reported the menu closing
  // as soon as they tried to hover a submenu item.  All the mega
  // items are already surfaced on the /resources page (Documentation,
  // Blog, Get Started, Trust, Academy, Multi-tenant Admin), so we
  // demote it to a plain link — one predictable click, zero hover
  // choreography.  (06/2026 Full Production Stabilization.)
  { label: 'Resources',  href: '/resources' },
  { label: 'Company',    href: '/company' },
  { label: 'Contact',    href: '/contact' },
]

const SiteHeader = () => {
  const pathname = usePathname() || '/'
  const [scrolled, setScrolled] = useState(false)
  const [openMega, setOpenMega] = useState(null)   // 'platform' | 'features' | 'resources' | null
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close mega-menu on route change
  useEffect(() => { setOpenMega(null); setMobileOpen(false) }, [pathname])

  const activeFor = (href) => href && (pathname === href || pathname.startsWith(href + '/'))

  const megaContent = (
    <>
      {openMega === 'platform' && <MegaGrid items={PLATFORM_MENU} onClose={() => setOpenMega(null)} cols={2} />}
      {/* Resources mega intentionally removed — trigger is now a
          direct link to /resources (see PRIMARY_NAV above). */}
    </>
  )

  return (
    <>
      {/* Skip-to-content for a11y */}
      <a href="#brand-main" className="sr-only focus:not-sr-only focus:fixed focus:z-[100] focus:top-3 focus:left-3 focus:rounded focus:bg-brand-gold focus:px-3 focus:py-2 focus:text-black focus:font-semibold">Skip to content</a>

      <header
        className={clsx(
          'fixed left-1/2 top-4 z-50 -translate-x-1/2 transition-all duration-300 ease-out',
          'w-[calc(100%-24px)] max-w-[1360px]',
        )}
        onMouseLeave={() => setOpenMega(null)}
      >
        <div
          className={clsx(
            'flex items-center justify-between gap-4 rounded-full border px-3 py-2.5 transition-all duration-300',
            scrolled
              ? 'border-white/10 bg-black/70 backdrop-blur-xl shadow-[0_18px_60px_-24px_rgba(0,0,0,0.9),0_1px_0_0_rgba(255,255,255,0.05)_inset]'
              : 'border-white/[0.06] bg-black/30 backdrop-blur-lg',
          )}
        >
          <BrandLogo size="sm" hideCloudOnMobile />

          {/* Primary nav — hidden on small screens */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {PRIMARY_NAV.map((item) => {
              const isActive = item.href ? activeFor(item.href) : false
              if (item.megaKey) {
                const open = openMega === item.megaKey
                return (
                  <button
                    key={item.label}
                    type="button"
                    onMouseEnter={() => setOpenMega(item.megaKey)}
                    onFocus={() => setOpenMega(item.megaKey)}
                    className={clsx(
                      'inline-flex items-center gap-0.5 rounded-full px-2.5 py-1.5 text-[12.5px] font-medium whitespace-nowrap transition-colors',
                      open ? 'bg-white/[0.06] text-white' : 'text-zinc-300 hover:text-white hover:bg-white/[0.04]',
                    )}
                    aria-expanded={open}
                  >
                    {item.label}
                    <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
                  </button>
                )
              }
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  onMouseEnter={() => setOpenMega(null)}
                  className={clsx(
                    'rounded-full px-2.5 py-1.5 text-[12.5px] font-medium whitespace-nowrap transition-colors',
                    isActive ? 'bg-white/[0.08] text-white' : 'text-zinc-300 hover:text-white hover:bg-white/[0.04]',
                  )}
                >
                  {item.label}
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center gap-1.5">
            {/* Login intentionally NOT shown on the master corporate site.
                Tenant customers sign in at their own workspace URL
                (yourbrand.kalkiai.trade/login, custom-domain.com/login, etc.).
                On kalkiai.trade itself, hitting /login would render the
                default tenant landing — confusing marketing visitors
                — so we route them to Schedule Demo / Launch Platform
                instead.  Reintroduce a real customer-portal login
                here once /login is host-branched. */}
            <GhostButton href="/contact#demo" size="sm" className="hidden xl:inline-flex whitespace-nowrap">Schedule Demo</GhostButton>
            <GradientButton href="/get-started" size="sm" className="whitespace-nowrap">Launch Platform</GradientButton>

            <button
              type="button"
              aria-label="Menu"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-white/[0.06]"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Mega-menu panel.
             NOTE: we intentionally use pt-3 (padding-top) instead of
             mt-3 (margin-top) here so the 12px "gap" between the nav
             button and the panel becomes part of the panel's own
             bounding box.  Otherwise moving the pointer from the
             button down into the panel crosses a 12px unhoverable
             void, which triggers onMouseLeave on the <header> and
             closes the menu prematurely (production regression that
             the user reported on Resources — same root cause applies
             to Platform).  padding preserves the visual gap without
             breaking the pointer path. */}
        {openMega && (
          <div
            className="absolute left-1/2 top-full z-40 w-[min(960px,calc(100vw-24px))] -translate-x-1/2 pt-3"
            onMouseEnter={() => setOpenMega(openMega)}
          >
            <div className="rounded-3xl border border-white/10 bg-black/85 backdrop-blur-2xl p-3 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.9)]">
              {megaContent}
            </div>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-xl lg:hidden overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-4 border-b border-white/5">
            <BrandLogo size="sm" hideCloudOnMobile />
            <button aria-label="Close menu" onClick={() => setMobileOpen(false)} className="h-9 w-9 rounded-full inline-flex items-center justify-center text-white hover:bg-white/[0.06]">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="px-4 py-6 space-y-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Platform</div>
              <div className="grid gap-2">
                {PLATFORM_MENU.map((i) => (
                  <Link key={i.href} href={i.href} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-white hover:border-brand-gold/30">
                    <i.icon className="h-4 w-4 text-brand-gold" />
                    <span className="text-sm font-medium">{i.label}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Resources</div>
              <div className="grid gap-2">
                {RESOURCES_MENU.map((i) => (
                  <Link key={i.href} href={i.href} className="flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-white hover:border-brand-gold/30">
                    <i.icon className="h-4 w-4 text-brand-gold" />
                    <span className="text-sm font-medium">{i.label}</span>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Explore</div>
              <div className="grid gap-2">
                <Link href="/pricing"  className="px-4 py-3 rounded-xl border border-white/5 text-white">Pricing</Link>
                <Link href="/company"  className="px-4 py-3 rounded-xl border border-white/5 text-white">Company</Link>
                <Link href="/contact"  className="px-4 py-3 rounded-xl border border-white/5 text-white">Contact</Link>
                <Link href="/trust"    className="px-4 py-3 rounded-xl border border-white/5 text-white">Trust Center</Link>
              </div>
            </div>
            <div className="pt-4 border-t border-white/5 flex flex-col gap-3">
              <GhostButton href="/contact#demo">Schedule Demo</GhostButton>
              <GradientButton href="/get-started">Launch Platform</GradientButton>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Mega grid — reusable within the mega-menu panel ─────────────
const MegaGrid = ({ items, cols = 2, onClose }) => (
  <div className={clsx('grid gap-1', cols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2')}>
    {items.map((i) => (
      <Link
        key={i.href}
        href={i.href}
        onClick={onClose}
        className="group flex items-start gap-3 rounded-2xl p-4 hover:bg-white/[0.04]"
      >
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-brand-gold/25 bg-brand-gold/10 text-brand-gold group-hover:border-brand-gold/50">
          <i.icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white">{i.label}</div>
          <div className="text-[12px] text-zinc-400">{i.desc}</div>
        </div>
      </Link>
    ))}
  </div>
)

export default SiteHeader
